import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import axios from 'axios';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Trophy, Flame, Target, Star, ChevronRight } from 'lucide-react';

const GamificationStats = ({ compact = false }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/gamification/my-stats`);
      setStats(response.data);
    } catch (e) {
      console.error('Failed to fetch gamification stats:', e);
    } finally {
      setLoading(false);
    }
  };

  // Calculate level based on points
  const getLevel = (points) => {
    if (points >= 1000) return { level: 5, name: 'Legend', color: 'text-yellow-400', next: null };
    if (points >= 500) return { level: 4, name: 'Expert', color: 'text-purple-400', next: 1000 };
    if (points >= 200) return { level: 3, name: 'Regular', color: 'text-blue-400', next: 500 };
    if (points >= 50) return { level: 2, name: 'Newcomer', color: 'text-green-400', next: 200 };
    return { level: 1, name: 'Rookie', color: 'text-slate-400', next: 50 };
  };

  const getLevelProgress = (points) => {
    const { next } = getLevel(points);
    if (!next) return 100;
    const prevThreshold = points >= 500 ? 500 : points >= 200 ? 200 : points >= 50 ? 50 : 0;
    return ((points - prevThreshold) / (next - prevThreshold)) * 100;
  };

  if (loading || !stats) {
    return null;
  }

  const levelInfo = getLevel(stats.points);
  const progress = getLevelProgress(stats.points);

  if (compact) {
    return (
      <div
        onClick={() => navigate('/leaderboard')}
        className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-600/20 to-indigo-600/20 rounded-full cursor-pointer hover:from-purple-600/30 hover:to-indigo-600/30 transition-all border border-purple-500/30"
      >
        <Flame className="w-4 h-4 text-orange-400" />
        <span className="text-sm font-semibold text-yellow-400">{stats.points}</span>
        <span className="text-xs text-slate-400">pts</span>
      </div>
    );
  }

  return (
    <Card className="glass p-6 border border-purple-500/30 fade-in">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-400" />
          Your Progress
        </h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/leaderboard')}
          className="text-purple-400 hover:text-purple-300 hover:bg-purple-500/10"
        >
          Leaderboard
          <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </div>

      {/* Level Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Star className={`w-5 h-5 ${levelInfo.color}`} />
            <span className={`font-semibold ${levelInfo.color}`}>
              Level {levelInfo.level}: {levelInfo.name}
            </span>
          </div>
          <span className="text-sm text-slate-400">
            {stats.points} / {levelInfo.next || 'MAX'} pts
          </span>
        </div>
        <Progress value={progress} className="h-2 bg-slate-700" />
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center p-2 bg-slate-800/50 rounded-lg">
          <div className="text-xl font-bold text-purple-400">#{stats.rank}</div>
          <div className="text-xs text-slate-400">Rank</div>
        </div>
        <div className="text-center p-2 bg-slate-800/50 rounded-lg">
          <div className="text-xl font-bold text-green-400">{stats.checkin_count}</div>
          <div className="text-xs text-slate-400">Check-ins</div>
        </div>
        <div className="text-center p-2 bg-slate-800/50 rounded-lg">
          <div className="text-xl font-bold text-blue-400">{stats.unique_bars_visited}</div>
          <div className="text-xs text-slate-400">Bars</div>
        </div>
      </div>

      {/* Badges */}
      {stats.badges && stats.badges.length > 0 && (
        <div>
          <p className="text-sm text-slate-400 mb-2 flex items-center gap-1">
            <Target className="w-4 h-4" />
            Badges Earned ({stats.badges.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {stats.badges.slice(0, 5).map((badge, idx) => (
              <div
                key={idx}
                className="flex items-center gap-1 px-2 py-1 bg-purple-500/20 rounded-full text-xs"
                title={badge.description}
              >
                <span>{badge.icon}</span>
                <span className="text-purple-300">{badge.name}</span>
              </div>
            ))}
            {stats.badges.length > 5 && (
              <span className="text-xs text-slate-400 px-2 py-1">+{stats.badges.length - 5} more</span>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

export default GamificationStats;
